"use client";

import { getBreadcrumbPlaceholder } from "@/lib/breadcrumb-placeholders";
import { usePathname } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  ReactNode,
} from "react";

export interface BreadcrumbItem {
  label: string | React.ReactNode;
  href?: string;
  // Lets a page handle the click itself, for crumbs that only change its own URL state.
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

interface SetBreadcrumbsOptions {
  pending?: boolean;
}

interface BreadcrumbState {
  items: BreadcrumbItem[];
  pending: boolean;
}

interface BreadcrumbContextType {
  breadcrumbs: BreadcrumbItem[];
  isPending: boolean;
  setBreadcrumbs: (
    breadcrumbs:
      BreadcrumbItem[] | ((breadcrumbs: BreadcrumbItem[]) => BreadcrumbItem[]),
    options?: SetBreadcrumbsOptions,
  ) => void;
  clearBreadcrumbs: () => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(
  undefined,
);

export const useBreadcrumb = () => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error("useBreadcrumb must be used within a BreadcrumbProvider");
  }
  return context;
};

interface BreadcrumbProviderProps {
  children: ReactNode;
}

const getPlaceholderState = (pathname: string): BreadcrumbState => {
  const placeholder = getBreadcrumbPlaceholder(pathname);
  if (!placeholder) {
    return { items: [], pending: false };
  }

  return { items: [...placeholder.items], pending: true };
};

export const BreadcrumbProvider = ({ children }: BreadcrumbProviderProps) => {
  const pathname = usePathname();
  const [state, setState] = useState<BreadcrumbState>(() =>
    getPlaceholderState(pathname),
  );
  const prevPathnameRef = useRef(pathname);

  if (prevPathnameRef.current !== pathname) {
    prevPathnameRef.current = pathname;
    setState(getPlaceholderState(pathname));
  }

  const setBreadcrumbs = useCallback(
    (
      nextBreadcrumbs:
        | BreadcrumbItem[]
        | ((breadcrumbs: BreadcrumbItem[]) => BreadcrumbItem[]),
      options?: SetBreadcrumbsOptions,
    ) => {
      setState((current) => {
        const items =
          typeof nextBreadcrumbs === "function"
            ? nextBreadcrumbs(current.items)
            : nextBreadcrumbs;

        return {
          items,
          pending: options?.pending ?? false,
        };
      });
    },
    [],
  );

  const clearBreadcrumbs = useCallback(() => {
    setState(getPlaceholderState(prevPathnameRef.current));
  }, []);

  return (
    <BreadcrumbContext.Provider
      value={{
        breadcrumbs: state.items,
        isPending: state.pending,
        setBreadcrumbs,
        clearBreadcrumbs,
      }}
    >
      {children}
    </BreadcrumbContext.Provider>
  );
};
